import React from "react";
import { useRetrieveZonesIndicators, useSearch } from "@planzone/view-models";
import { InfiniteScrollRoot, Flag } from "@common/infiniteScroll";
import { Identifiable, Project as P, SearchQuery } from "@planzone/dto";

import { Loader } from "../loader";
import { ScrollPane } from "../scrollPane";
import { Section } from "../section";
import { Props } from "./types";
import { Zone } from "./zone";
import { Project } from "./zone/projects/project";
import styles from "./styles.scss";

function Zones({ search, selected, onSelect, isOpen }: Props) {
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    zones,
    canRetrieveNextZonesIndicators,
    index,
    retrieveZonesIndicators,
  } = useRetrieveZonesIndicators(
    { includeAllZones: true },
    "ascendingName",
    10,
  );

  const [query, setQuery] = React.useState<SearchQuery>({
    name: search,
    types: ["project"],
    start: 0,
    limit: 99999,
    includeAllProjects: true,
  });
  const {
    isRequestPending: isSearchPending,
    searchResults,
    search: doSearch,
  } = useSearch(query);

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveZonesIndicators({
        filter: { includeAllZones: true },
        sort: "ascendingName",
        size: 10,
        index: 0,
        view: "simple",
      });
  }, []);

  React.useEffect(() => {
    if (search) {
      const newQuery: SearchQuery = {
        name: search,
        types: ["project"],
        start: 0,
        limit: 99999,
        includeAllProjects: true,
      };
      setQuery(newQuery);
      doSearch(newQuery);
    }
  }, [search]);

  if (search) {
    return (
      <Section
        header={<div className={styles.header} />}
        list={
          <ScrollPane>
            {isSearchPending ? (
              <div className={styles.loader}>
                <Loader />
              </div>
            ) : (
              <div>
                {searchResults
                  .filter(
                    (r): r is { type: "project"; args: Identifiable<P> } =>
                      r.type === "project",
                  )
                  .map((r) => (
                    <Project
                      key={r.args.uuid}
                      project={r.args}
                      isSelected={selected?.uuid === r.args.uuid}
                      onSelect={() => onSelect(r.args)}
                    />
                  ))}
              </div>
            )}
          </ScrollPane>
        }
        isHeaderToggleDisabled
        isOpen={isOpen}
      />
    );
  }

  return (
    <Section
      header={<div className={styles.header} />}
      list={
        <ScrollPane>
          <InfiniteScrollRoot canFetchOnIdle>
            <div className={styles.list}>
              {isRequestPending && zones.length === 0 ? (
                <div className={styles.loader}>
                  <Loader />
                </div>
              ) : (
                [...zones]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((zone) => (
                    <Zone
                      key={zone.uuid}
                      search={search}
                      zone={zone}
                      selected={selected}
                      onSelect={onSelect}
                    />
                  ))
              )}
              {isRequestPending && zones.length > 0 && (
                <div className={styles.loader}>
                  <Loader />
                </div>
              )}
              <Flag
                name="assignToTaskZonesFlag"
                retrieveItems={() =>
                  canRetrieveNextZonesIndicators &&
                  !isRequestPending &&
                  retrieveZonesIndicators({
                    filter: { includeAllZones: true },
                    sort: "ascendingName",
                    size: 10,
                    index,
                    view: "simple",
                  })
                }
                isDisabled={!canRetrieveNextZonesIndicators || isRequestPending}
              />
            </div>
          </InfiniteScrollRoot>
        </ScrollPane>
      }
      isHeaderToggleDisabled
      isOpen={isOpen}
    />
  );
}

export { Zones };
